import { ThemeProvider as StyledComponentsThemeProvider } from "styled-components";
import { useTheme } from "@tomis-tech/chat-ui";

import { ToastProvider } from "@/hooks";
import { ChatWidget, ChatWidgetProps } from "@/views/ChatWidget";
import { FormOnlyWidget } from "@/views/FormOnlyWidget";
import { ErrorBoundary } from "./ErrorBoundary";
import { ChatWidgetLoadResponse } from "./types";

/** Props for the App component */
export interface AppProps extends ChatWidgetProps {
  /** Required form configuration when widget should be in form-only mode */
  requiredForm?: ChatWidgetLoadResponse["required_form"];
}

/**
 * React App parent component.
 *
 * Conditionally renders either:
 * - FormOnlyWidget: when requiredForm is present (form-only mode)
 * - ChatWidget: for normal chat mode
 */
export const App: React.FC<AppProps> = ({
  config,
  onReady,
  embed = false,
  feedback = "disabled",
  test_mode = false,
  conversationHistory,
  handoffStatus,
  forms,
  requiredForm,
}) => {
  // Determine if we should render form-only mode
  const isFormOnlyMode = requiredForm?.mode === "form_only" && requiredForm.key;

  return (
    <ErrorBoundary fallback={null}>
      <StyledComponentsThemeProviderLocal>
        <ToastProvider>
          {isFormOnlyMode ? (
            <FormOnlyWidget
              onReady={onReady}
              config={config}
              embed={embed}
              test_mode={test_mode}
              requiredFormKey={requiredForm.key}
              forms={forms || []}
            />
          ) : (
            <ChatWidget
              onReady={onReady}
              config={config}
              embed={embed}
              feedback={feedback}
              test_mode={test_mode}
              conversationHistory={conversationHistory}
              handoffStatus={handoffStatus}
              forms={forms}
            />
          )}
        </ToastProvider>
      </StyledComponentsThemeProviderLocal>
    </ErrorBoundary>
  );
};

const StyledComponentsThemeProviderLocal: React.FC<
  React.PropsWithChildren<any>
> = ({ children }) => {
  const theme = useTheme();

  return (
    <StyledComponentsThemeProvider theme={theme as any}>
      {children}
    </StyledComponentsThemeProvider>
  );
};
